import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as agentApi from '@/lib/agents/api';
import type { Agent, AgentType } from '@/lib/agents/types';
import { AGENT_TYPE_INFO, LLM_PROVIDER_INFO } from '@/lib/agents/types';
import {
  Search,
  Plus,
  Bot,
  MessageSquare,
  BarChart,
  Code,
  Headphones,
  Globe,
  ListTodo,
  Settings,
  Play,
  MoreVertical,
  Trash2,
  Edit,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const Route = createFileRoute('/_dashboard/agents/')({
  component: AgentsPage,
});

const AGENT_TYPE_ICONS: Record<AgentType, React.ElementType> = {
  chat: MessageSquare,
  research: Search,
  task: ListTodo,
  'data-analysis': BarChart,
  'code-assistant': Code,
  support: Headphones,
  'web-browsing': Globe,
  'multi-agent': Users,
  custom: Settings,
};

const AGENT_TYPE_COLORS: Record<AgentType, string> = {
  chat: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  research: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  task: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  'data-analysis': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  'code-assistant': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
  support: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  'web-browsing': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  'multi-agent': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  custom: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
};

function AgentsPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agentApi.listAgents({
        type: selectedType !== 'all' ? (selectedType as AgentType) : undefined,
        active: showActiveOnly ? true : undefined,
        search: searchQuery || undefined,
        limit: pagination.limit,
        offset: pagination.offset,
      });

      setAgents(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  }, [selectedType, showActiveOnly, searchQuery, pagination.limit, pagination.offset]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, [selectedType, showActiveOnly, searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchAgents();
  };

  const handleDelete = async (agentId: string, agentName: string) => {
    if (!confirm(`Are you sure you want to delete "${agentName}"?`)) {
      return;
    }

    try {
      await agentApi.deleteAgent(agentId);
      toast.success('Agent deleted');
      fetchAgents();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete agent');
    }
  };

  const handleLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Agents</h1>
          <p className="text-muted-foreground">
            Create and manage intelligent agents for various tasks
          </p>
        </div>
        <Button onClick={() => navigate({ to: '/agents/create' })}>
          <Plus className="mr-2 h-4 w-4" />
          Create Agent
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Agent type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(AGENT_TYPE_INFO).map(([type, info]) => (
                  <SelectItem key={type} value={type}>
                    {info.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant={showActiveOnly ? 'default' : 'outline'}
              onClick={() => setShowActiveOnly(!showActiveOnly)}
            >
              Active Only
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Total Agents</p>
                <p className="text-2xl font-bold">{pagination.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(AGENT_TYPE_INFO).slice(0, 3).map(([type, info]) => {
          const count = agents.filter((a) => a.agent_type === type).length;
          const Icon = AGENT_TYPE_ICONS[type as AgentType];
          return (
            <Card key={type}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{info.label}s</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Agents Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No agents found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || selectedType !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first agent to get started'}
            </p>
            <Button onClick={() => navigate({ to: '/agents/create' })}>
              <Plus className="mr-2 h-4 w-4" />
              Create Agent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent) => {
              const Icon = AGENT_TYPE_ICONS[agent.agent_type];
              const typeInfo = AGENT_TYPE_INFO[agent.agent_type];
              const providerInfo = LLM_PROVIDER_INFO[agent.config.llmProvider];

              return (
                <Card
                  key={agent.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate({ to: '/agents/$id', params: { id: agent.id } })}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${AGENT_TYPE_COLORS[agent.agent_type]}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{agent.name}</CardTitle>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {typeInfo.label}
                          </Badge>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate({ to: '/agents/$id/chat', params: { id: agent.id } });
                            }}
                          >
                            <Play className="mr-2 h-4 w-4" />
                            Chat
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate({ to: '/agents/$id', params: { id: agent.id } });
                            }}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(agent.id, agent.name);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="line-clamp-2 mb-3">
                      {agent.description || typeInfo.description}
                    </CardDescription>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="secondary" className="text-xs">
                        {providerInfo.label}
                      </Badge>
                      {agent.config.model && (
                        <Badge variant="outline" className="text-xs">
                          {agent.config.model}
                        </Badge>
                      )}
                      {!agent.is_active && (
                        <Badge variant="destructive" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {pagination.hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
